/** Electricity prices: the staff-maintained tariff tables (Canada, GB cap, imported US rates). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArrowLeft, CalendarPlus, History, Pencil, Plus, Search, ShieldAlert, ShieldCheck, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Button, Card, ChipsInput, Empty, Field, Modal, PageHead, Segmented, Select, Spinner, Switch, TextInput, useToast } from "../../components/ui";
import { CURRENCIES, PriceFields, blankTariff, priceProblems } from "../../components/PriceFields";
import { useT, type Translate } from "../../i18n/I18nProvider";
import { DirIcon } from "../../i18n/mirror";
import { useFmt, type Fmt } from "./format";
import { useDebounced } from "./Orgs";
import { RefreshPrices } from "./RefreshPrices";

type Record = S["UtilityTariff"];
type Tariff = S["Tariff"];
type Country = "CA" | "GB" | "US" | "all";

const SOURCES = ["", "staff", "ofgem", "urdb", "eia_861", "eia_861_state", "octopus"] as const;
const PERIOD_COLOURS = ["#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#10b981", "#ec4899", "#14b8a6", "#64748b"];

// --------------------------------------------------------------- summaries
/** The tariff summaries in the reader's language (the same shapes as components/PriceFields' English ones). */
function tariffText(t: Translate, fmt: Fmt) {
  /** A price per kWh in the currency's minor unit (¢ or p). */
  const perKwh = (rate: number | null | undefined, currency: string): string =>
    rate == null ? "—" : t("admin.tariffs.perKwh", { value: fmt.num(rate * 100, 2), currency });
  const rates = (x: Tariff): number[] => {
    const e = x.energy;
    if (e.type === "flat") return [e.rate ?? 0];
    if (e.type === "tiered") return (e.tiers ?? []).map((r) => r.rate);
    if (e.type === "tou") return (e.seasons ?? []).flatMap((season) => [season.base_rate, ...season.periods.map((p) => p.rate)]);
    return (e.periods ?? []).flatMap((p) => p.tiers.map((r) => r.rate));
  };
  const energy = (x: Tariff): string => {
    const values = rates(x);
    const low = Math.min(...values);
    const high = Math.max(...values);
    const span = low === high ? perKwh(low, x.currency) : t("admin.tariffs.rateRange", { low: perKwh(low, x.currency), high: perKwh(high, x.currency) });
    const e = x.energy;
    if (e.type === "flat") return t("admin.tariffs.energyFlat", { span });
    if (e.type === "tiered") return t("admin.tariffs.energyTiered", { count: e.tiers?.length ?? 0, span });
    if (e.type === "tou") return t("admin.tariffs.energyTou", { span });
    const tiered = (e.periods ?? []).some((p) => p.tiers.length > 1);
    return t("admin.tariffs.energySchedule", { count: e.periods?.length ?? 0, tiered: tiered ? "yes" : "no", span });
  };
  const perMonth = (amount: number) => t("admin.tariffs.perMonth", { amount: fmt.num(amount, 2) });
  const fixed = (x: Tariff): string => {
    const monthly = x.fixed_monthly ? perMonth(x.fixed_monthly) : null;
    const daily = x.fixed_daily ? t("admin.tariffs.perDay", { value: fmt.num(x.fixed_daily * 100, 2), currency: x.currency }) : null;
    if (monthly && daily) return t("admin.tariffs.fixedBoth", { monthly, daily });
    return monthly ?? daily ?? "—";
  };
  const exports = (x: Tariff): string => {
    const e = x.export;
    if (e.mode === "net_metering")
      return e.credit_expiry_months ? t("admin.tariffs.exportNetMeteringCredits", { months: e.credit_expiry_months }) : t("admin.tariffs.exportNetMetering");
    return t("admin.tariffs.exportPaid", {
      mode: e.mode,
      rateKind: e.rate_schedule ? "hourly" : e.rate == null ? "period" : "rate",
      rate: perKwh(e.rate, x.currency),
    });
  };
  return { perKwh, energy, fixed, exports, perMonth };
}

function useTariffText() {
  const t = useT();
  const fmt = useFmt();
  return useMemo(() => tariffText(t, fmt), [t, fmt]);
}

function VerifiedBadge({ record }: { record: Record }) {
  const t = useT();
  if (record.retired_at) return <Badge>{t("admin.tariffs.badgeRetired")}</Badge>;
  return record.verified ? <Badge tone="ok">{t("admin.tariffs.badgeVerified")}</Badge> : <Badge tone="warn">{t("admin.tariffs.badgeToCheck")}</Badge>;
}

// ------------------------------------------------------------------- list
export function AdminTariffs() {
  const navigate = useNavigate();
  const t = useT();
  const { date, num } = useFmt();
  const summary = useTariffText();
  const [country, setCountry] = useState<Country>("CA");
  const [source, setSource] = useState<(typeof SOURCES)[number]>("");
  const [q, setQ] = useState("");
  const [retired, setRetired] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [creating, setCreating] = useState(false);
  const text = useDebounced(q.trim());
  const list = useQuery({
    queryKey: ["admin-tariffs", country, source, text, retired],
    queryFn: () =>
      unwrap(
        api.GET("/v1/admin/tariffs", {
          params: { query: { country: country === "all" ? undefined : country, source: source || undefined, q: text || undefined, include_retired: retired || undefined } },
        }),
      ),
    placeholderData: (prev) => prev,
  });
  const items = (list.data?.items ?? []).filter((r) => !unverified || !r.verified);
  const toCheck = (list.data?.items ?? []).filter((r) => !r.verified && !r.retired_at).length;
  return (
    <>
      <PageHead
        title={t("admin.tariffs.title")}
        description={t("admin.tariffs.description")}
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
            {t("admin.tariffs.newTariff")}
          </Button>
        }
      />
      <RefreshPrices />
      {toCheck > 0 && (
        <Alert tone="warn" title={t("admin.tariffs.toCheckTitle", { count: toCheck })}>
          {t("admin.tariffs.toCheckBody")}
        </Alert>
      )}
      <Card pad={false}>
        <div className="toolbar">
          <Segmented<Country>
            value={country}
            onChange={setCountry}
            options={[
              { value: "CA", label: t("admin.tariffs.countryCanada") },
              { value: "US", label: t("admin.tariffs.countryUs") },
              { value: "GB", label: t("admin.tariffs.countryGb") },
              { value: "all", label: t("admin.tariffs.countryAll") },
            ]}
          />
          <div className="input-icon">
            <Search />
            <input className="input" placeholder={t("admin.tariffs.search")} aria-label={t("admin.tariffs.search")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div style={{ width: 150 }}>
            <Select value={source} onChange={setSource} ariaLabel={t("admin.tariffs.source")} options={SOURCES.map((s) => ({ value: s, label: s ? s : t("admin.tariffs.anySource") }))} />
          </div>
          <Switch checked={unverified} onChange={setUnverified} label={t("admin.tariffs.toCheckOnly")} />
          <Switch checked={retired} onChange={setRetired} label={t("admin.tariffs.showRetired")} />
          {list.data && <span className="small faint">{t("admin.tariffs.shown", { count: items.length })}</span>}
        </div>
        {list.isLoading ? (
          <Spinner />
        ) : list.error ? (
          <div className="card-body">
            <Alert tone="danger">{errorMessage(list.error)}</Alert>
          </div>
        ) : !items.length ? (
          <Empty icon={<Zap />} title={t("admin.tariffs.emptyTitle")}>
            {t("admin.tariffs.emptyBody")}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("admin.tariffs.colTariff")}</th>
                  <th className="hide-sm">{t("admin.tariffs.colWhere")}</th>
                  <th>{t("admin.tariffs.colEffective")}</th>
                  <th>{t("admin.tariffs.energy")}</th>
                  <th className="hide-sm">{t("admin.tariffs.colFixed")}</th>
                  <th className="hide-sm">{t("admin.tariffs.exports")}</th>
                  <th>{t("admin.tariffs.colStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => navigate(`/admin/tariffs/${r.id}`)}>
                    <td>
                      <div className="cell-main" dir="auto">{r.name}</div>
                      <div className="cell-sub mono" dir="auto">
                        {t("admin.tariffs.recordMeta", { slug: r.slug, version: num(r.version), source: r.source ?? "", hasSource: r.source ? "yes" : "no" })}
                      </div>
                    </td>
                    <td className="hide-sm" dir="auto">{[...r.jurisdictions, ...(r.service_areas ?? [])].join(", ") || "—"}</td>
                    <td className="small">
                      {date(r.effective_from)}
                      <div className="faint">{r.effective_to ? t("admin.tariffs.effectiveTo", { date: date(r.effective_to) }) : t("admin.tariffs.openEnded")}</div>
                    </td>
                    <td>{summary.energy(r.structure)}</td>
                    <td className="hide-sm">{summary.fixed(r.structure)}</td>
                    <td className="hide-sm small">{summary.exports(r.structure)}</td>
                    <td>
                      <VerifiedBadge record={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {creating && <TariffForm onClose={() => setCreating(false)} onSaved={(r) => navigate(`/admin/tariffs/${r.id}`)} />}
    </>
  );
}

// ----------------------------------------------------------------- detail
export function AdminTariffDetail() {
  const { tariffId = "" } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const tr = useT();
  const fmt = useFmt();
  const { date, num } = fmt;
  const summary = useTariffText();
  const [editing, setEditing] = useState<"version" | "period" | null>(null);
  const versions = useQuery({
    queryKey: ["admin-tariff-versions", tariffId],
    queryFn: () => unwrap(api.GET("/v1/admin/tariffs/{tariff_id}/versions", { params: { path: { tariff_id: tariffId } } })),
  });
  const latest = versions.data?.items[0];
  const [notes, setNotes] = useState("");
  useEffect(() => setNotes(latest?.notes ?? ""), [latest?.id, latest?.notes]);
  const review = useMutation({
    mutationFn: (body: S["TariffRecordUpdate"]) => unwrap(api.PATCH("/v1/admin/tariffs/{tariff_id}", { params: { path: { tariff_id: tariffId } }, body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tariff-versions", tariffId] });
      qc.invalidateQueries({ queryKey: ["admin-tariffs"] });
      toast(tr("admin.tariffs.reviewSaved"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const retire = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/admin/tariffs/{tariff_id}", { params: { path: { tariff_id: tariffId } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tariffs"] });
      toast(tr("admin.tariffs.retired"));
      navigate("/admin/tariffs");
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (versions.isLoading) return <Spinner />;
  if (versions.error || !latest) return <Alert tone="danger">{errorMessage(versions.error) || tr("admin.tariffs.notFound")}</Alert>;
  const t = latest.structure;
  return (
    <>
      <PageHead
        crumbs={
          <Link to="/admin/tariffs">
            <DirIcon><ArrowLeft size={12} style={{ verticalAlign: -1 }} /></DirIcon> {tr("admin.tariffs.title")}
          </Link>
        }
        title={
          <span className="row" style={{ gap: 12 }}>
            <span dir="auto">{latest.name}</span>
            <VerifiedBadge record={latest} />
          </span>
        }
        description={
          <span className="mono small" dir="auto">
            {tr("admin.tariffs.detailMeta", {
              slug: latest.slug,
              version: num(latest.version),
              jurisdictions: latest.jurisdictions.join(", "),
              utility: latest.utility ?? "",
              hasUtility: latest.utility ? "yes" : "no",
            })}
          </span>
        }
        actions={
          !latest.retired_at && (
            <>
              <Button icon={<Pencil />} onClick={() => setEditing("version")}>
                {tr("admin.tariffs.correctPrices")}
              </Button>
              <Button icon={<CalendarPlus />} onClick={() => setEditing("period")}>
                {tr("admin.tariffs.newPeriod")}
              </Button>
              <Button variant="ghost" icon={<Archive />} loading={retire.isPending} onClick={() => window.confirm(tr("admin.tariffs.retireConfirm")) && retire.mutate()}>
                {tr("admin.tariffs.retire")}
              </Button>
            </>
          )
        }
      />
      <div className="split-wide">
        <div className="stack">
          <Card
            title={tr("admin.tariffs.prices")}
            description={
              latest.effective_to
                ? tr("admin.tariffs.pricesPeriod", { currency: t.currency, from: date(latest.effective_from), to: date(latest.effective_to) })
                : tr("admin.tariffs.pricesOpenEnded", { currency: t.currency, from: date(latest.effective_from) })
            }
          >
            <dl className="kv">
              <dt>{tr("admin.tariffs.energy")}</dt>
              <dd>{summary.energy(t)}</dd>
              <dt>{tr("admin.tariffs.fixedCharges")}</dt>
              <dd>{summary.fixed(t)}</dd>
              <dt>{tr("admin.tariffs.minimumBill")}</dt>
              <dd>{t.minimum_monthly ? summary.perMonth(t.minimum_monthly) : "—"}</dd>
              <dt>{tr("admin.tariffs.tax")}</dt>
              <dd>{fmt.percent((t.tax_rate ?? 0) * 100, 3)}</dd>
              <dt>{tr("admin.tariffs.exports")}</dt>
              <dd>{summary.exports(t)}</dd>
            </dl>
            <RateTable tariff={t} />
          </Card>
          {t.energy.type === "schedule" && t.energy.weekday_schedule && (
            <Card title={tr("admin.tariffs.schedule")} description={tr("admin.tariffs.scheduleDescription")}>
              <ScheduleGrid tariff={t} grid={t.energy.weekday_schedule} label={tr("admin.tariffs.weekdays")} />
              {t.energy.weekend_schedule && <ScheduleGrid tariff={t} grid={t.energy.weekend_schedule} label={tr("admin.tariffs.weekends")} />}
            </Card>
          )}
        </div>
        <div className="stack">
          <Card title={tr("admin.tariffs.review")} description={tr("admin.tariffs.reviewDescription")}>
            <div className="stack">
              <Switch checked={!!latest.verified} disabled={review.isPending} onChange={(v) => review.mutate({ verified: v })} label={latest.verified ? tr("admin.tariffs.verifiedByStaff") : tr("admin.tariffs.notVerified")} />
              <Field label={tr("admin.tariffs.notes")}>
                <textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
              <div className="row">
                <Button disabled={notes === (latest.notes ?? "")} loading={review.isPending} onClick={() => review.mutate({ notes: notes || null })}>
                  {tr("admin.tariffs.saveNotes")}
                </Button>
              </div>
              <div className="small faint">
                <FormattedMessage
                  id="admin.tariffs.sourceLine"
                  values={{
                    source: <span dir="auto">{latest.source ?? "—"}</span>,
                    hasUrl: latest.source_url ? "yes" : "no",
                    link: (c) => (
                      <a href={latest.source_url ?? undefined} target="_blank" rel="noreferrer">
                        {c}
                      </a>
                    ),
                    hasRetrieved: latest.retrieved_at ? "yes" : "no",
                    retrieved: date(latest.retrieved_at),
                  }}
                />
              </div>
            </div>
          </Card>
          <Card title={tr("admin.tariffs.versions")} description={tr("admin.tariffs.versionsDescription")} actions={<History size={16} />}>
            <ul className="stack" style={{ listStyle: "none", margin: 0, padding: 0, gap: 8 }}>
              {versions.data!.items.map((v) => (
                <li key={v.id} className="row" style={{ justifyContent: "space-between" }}>
                  <span>
                    <b>{tr("admin.tariffs.version", { version: num(v.version) })}</b> <span className="small faint">{date(v.created_at, true)}</span>
                  </span>
                  <span className="small">{summary.energy(v.structure)}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
      {editing && (
        <TariffForm
          base={latest}
          newPeriod={editing === "period"}
          onClose={() => setEditing(null)}
          onSaved={(r) => {
            qc.invalidateQueries({ queryKey: ["admin-tariff-versions"] });
            qc.invalidateQueries({ queryKey: ["admin-tariffs"] });
            setEditing(null);
            navigate(`/admin/tariffs/${r.id}`);
          }}
        />
      )}
    </>
  );
}

function RateTable({ tariff: x }: { tariff: Tariff }) {
  const t = useT();
  const { num } = useFmt();
  const { perKwh } = useTariffText();
  const e = x.energy;
  const rows: { name: string; upTo: string; rate: string; sell: string }[] = [];
  const tierRows = (name: string, tiers: S["TariffTier"][]) => {
    let lower = 0;
    for (const tier of tiers) {
      rows.push({
        name,
        upTo:
          tier.up_to_kwh == null
            ? lower
              ? t("admin.tariffs.tierOver", { lower: num(lower) })
              : t("admin.tariffs.tierAll")
            : t("admin.tariffs.tierRange", { lower: num(lower), upper: num(tier.up_to_kwh) }),
        rate: perKwh(tier.rate, x.currency),
        sell: perKwh(tier.sell_rate, x.currency),
      });
      lower = tier.up_to_kwh ?? lower;
    }
  };
  if (e.type === "tiered") tierRows(t("admin.tariffs.allHours"), e.tiers ?? []);
  if (e.type === "schedule") (e.periods ?? []).forEach((p, i) => tierRows(p.name || t("admin.tariffs.periodNumber", { n: i + 1 }), p.tiers));
  if (e.type === "tou")
    for (const s of e.seasons ?? []) {
      rows.push({ name: t("admin.tariffs.seasonOtherHours", { season: s.name }), upTo: "—", rate: perKwh(s.base_rate, x.currency), sell: "—" });
      for (const p of s.periods)
        rows.push({
          name: t("admin.tariffs.seasonPeriod", { season: s.name, period: p.name, hours: p.hours.map((h) => `${h.start}–${h.end}`).join(", ") }),
          upTo: "—",
          rate: perKwh(p.rate, x.currency),
          sell: "—",
        });
    }
  if (!rows.length) return null;
  return (
    <div className="table-wrap" style={{ marginTop: 12 }}>
      <table className="table">
        <thead>
          <tr>
            <th>{t("admin.tariffs.colPeriod")}</th>
            <th>{t("admin.tariffs.colMonthlyKwh")}</th>
            <th className="num">{t("admin.tariffs.colRate")}</th>
            <th className="num">{t("admin.tariffs.colExport")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td dir="auto">{r.name}</td>
              <td className="small">{r.upTo}</td>
              <td className="num">{r.rate}</td>
              <td className="num">{r.sell}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScheduleGrid({ tariff: x, grid, label }: { tariff: Tariff; grid: number[][]; label: string }) {
  const t = useT();
  const intl = useIntl();
  const { num } = useFmt();
  const { perKwh } = useTariffText();
  const periods = x.energy.periods ?? [];
  const month = (m: number, style: "narrow" | "long") => intl.formatDate(new Date(2000, m, 1), { month: style });
  const periodName = (p: number) => periods[p]?.name || t("admin.tariffs.periodNumber", { n: p + 1 });
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="small muted" style={{ marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "18px repeat(24, minmax(0, 1fr))", gap: 2, fontSize: 10 }}>
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="faint" style={{ textAlign: "center" }}>
            {h % 3 === 0 ? num(h) : ""}
          </span>
        ))}
        {grid.map((row, m) => [
          <span key={`m${m}`} className="faint">
            {month(m, "narrow")}
          </span>,
          ...row.map((p, h) => (
            <span
              key={`${m}-${h}`}
              title={t("admin.tariffs.scheduleCell", {
                month: month(m, "long"),
                hour: intl.formatTime(new Date(2000, 0, 1, h), { hour: "numeric", minute: "2-digit" }),
                period: periodName(p),
                rate: perKwh(periods[p]?.tiers[0]?.rate, x.currency),
              })}
              style={{ height: 14, borderRadius: 2, background: PERIOD_COLOURS[p % PERIOD_COLOURS.length], opacity: 0.85 }}
            />
          )),
        ])}
      </div>
      <div className="row small" style={{ marginTop: 8, gap: 14 }}>
        {periods.map((p, i) => (
          <span key={i} className="row" style={{ gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: PERIOD_COLOURS[i % PERIOD_COLOURS.length] }} />
            {t("admin.tariffs.periodLegend", { period: periodName(i), rate: perKwh(p.tiers[0]?.rate, x.currency) })}
          </span>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- form


function TariffForm({ base, newPeriod, onClose, onSaved }: { base?: Record; newPeriod?: boolean; onClose: () => void; onSaved: (r: Record) => void }) {
  const toast = useToast();
  const tr = useT();
  const [slug, setSlug] = useState(base?.slug ?? "");
  const [name, setName] = useState(base?.name ?? "");
  const [utility, setUtility] = useState(base?.utility ?? "");
  const [jurisdictions, setJurisdictions] = useState<string[]>(base?.jurisdictions ?? []);
  const [areas, setAreas] = useState<string[]>(base?.service_areas ?? []);
  const [from, setFrom] = useState(newPeriod ? "" : base?.effective_from ?? "");
  const [to, setTo] = useState(newPeriod ? "" : base?.effective_to ?? "");
  const [sourceUrl, setSourceUrl] = useState(base?.source_url ?? "");
  const [verified, setVerified] = useState<boolean>(base?.verified ?? false);
  const [notes, setNotes] = useState(base?.notes ?? "");
  const [t, setT] = useState<Tariff>(() => structuredClone(base?.structure ?? blankTariff("", "CAD")));
  const [tried, setTried] = useState(false);
  const problems = priceProblems(t);

  const save = useMutation({
    mutationFn: (body: S["UtilityTariffCreate"]) => unwrap(api.POST("/v1/admin/tariffs", { body })),
    onSuccess: (r) => {
      toast(base ? (newPeriod ? tr("admin.tariffs.periodAdded") : tr("admin.tariffs.versionSaved")) : tr("admin.tariffs.created"));
      onSaved(r);
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const submit = () => {
    setTried(true);
    if (Object.keys(problems).length) return;
    save.mutate({
      slug,
      name,
      utility: utility || null,
      jurisdictions,
      service_areas: areas,
      effective_from: from,
      effective_to: to || null,
      source: base?.source ?? "staff",
      source_url: sourceUrl || null,
      verified,
      notes: notes || null,
      structure: { ...t, name, utility: utility || null, effective_from: from || null, effective_to: to || null },
    });
  };
  const title = !base ? tr("admin.tariffs.newTariff") : newPeriod ? tr("admin.tariffs.newPeriodTitle") : tr("admin.tariffs.correctPricesTitle");
  return (
    <Modal
      title={title}
      onClose={onClose}
      width={760}
      footer={
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose}>
            {tr("admin.common.cancel")}
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!slug || !name || !from} onClick={submit}>
            {tr("admin.common.save")}
          </Button>
        </div>
      }
    >
      <div className="stack">
        <div className="form-grid">
          <Field label={tr("admin.tariffs.fieldName")}>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={tr("admin.tariffs.fieldSlug")} hint={base ? tr("admin.tariffs.fieldSlugFixed") : tr("admin.tariffs.fieldSlugHint")}>
            <TextInput value={slug} disabled={!!base} onChange={(e) => setSlug(e.target.value.toLowerCase())} />
          </Field>
          <Field label={tr("admin.tariffs.fieldUtility")}>
            <TextInput value={utility} onChange={(e) => setUtility(e.target.value)} />
          </Field>
          <Field label={tr("admin.tariffs.fieldCurrency")}>
            <Select value={t.currency} onChange={(v) => setT({ ...t, currency: v })} options={CURRENCIES} />
          </Field>
          <Field label={tr("admin.tariffs.fieldJurisdictions")} hint={tr("admin.tariffs.fieldJurisdictionsHint")}>
            <ChipsInput values={jurisdictions} onChange={setJurisdictions} normalize={(v) => v.trim().toUpperCase()} validate={(v) => (/^[A-Z]{2}(-[A-Z0-9]{1,3})?$/.test(v) ? null : tr("admin.tariffs.fieldJurisdictionsInvalid"))} />
          </Field>
          <Field label={tr("admin.tariffs.fieldServiceAreas")} hint={tr("admin.tariffs.fieldServiceAreasHint")}>
            <ChipsInput values={areas} onChange={setAreas} normalize={(v) => v.trim().toLowerCase()} />
          </Field>
          <Field label={tr("admin.tariffs.fieldEffectiveFrom")}>
            <input className="input" type="date" value={from} disabled={!!base && !newPeriod} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={tr("admin.tariffs.fieldEffectiveTo")} hint={tr("admin.tariffs.fieldEffectiveToHint")}>
            <input className="input" type="date" value={to ?? ""} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>

        <PriceFields value={t} onChange={setT} showErrors={tried} allowSchedule />

        <div className="form-grid">
          <Field label={tr("admin.tariffs.fieldSourceUrl")} className="span-2">
            <TextInput value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder={tr("admin.tariffs.urlPlaceholder")} />
          </Field>
          <Field label={tr("admin.tariffs.notes")} className="span-2">
            <textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
        <Switch checked={!!verified} onChange={setVerified} label={verified ? <><ShieldCheck size={14} /> {tr("admin.tariffs.checked")}</> : <><ShieldAlert size={14} /> {tr("admin.tariffs.notChecked")}</>} />
      </div>
    </Modal>
  );
}
